import { useState, useCallback, useRef } from 'react';
import { disconnectVoiceClient, VoicePreference } from '../../services/cxone-sdk.service';
import { resolveVoicePreferenceForConnection } from '../../utils/config';
import { logger } from '../../utils/logger';
import { CXoneState } from '../../types';
import { CxoneSessionRefs, initialCxoneState } from './types';
import { isCxoneSessionError, MAX_RECONNECT_ATTEMPTS } from './cxone-session-error.util';

interface UseCxoneReconnectOptions {
  refs: CxoneSessionRefs;
  initializeWithVoicePreference: (preference: VoicePreference) => Promise<void>;
  setState: React.Dispatch<React.SetStateAction<CXoneState>>;
  setNeedsVoicePreference: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCxoneReconnect({
  refs,
  initializeWithVoicePreference,
  setState,
  setNeedsVoicePreference,
}: UseCxoneReconnectOptions) {
  const [isSessionInvalid, setIsSessionInvalid] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectAttemptRef = useRef(0);

  const reconnect = useCallback(async () => {
    logger.lifecycle('[CXone] Reconnect started');

    setIsReconnecting(true);
    setIsSessionInvalid(false);
    refs.initializedRef.current = false;
    refs.initializingRef.current = false;

    try {
      disconnectVoiceClient();
    } catch (e) {
      logger.warn('[CXone] Error during disconnect cleanup:', e);
    }

    await new Promise((resolve) => setTimeout(resolve, 500));

    const preference = resolveVoicePreferenceForConnection(refs.voicePreferenceRef.current);
    if (preference) {
      logger.debug('[CXone] Reconnecting with voice preference:', preference.type);
      try {
        await initializeWithVoicePreference(preference);
        reconnectAttemptRef.current = 0;
        logger.lifecycle('[CXone] Reconnect successful');
      } catch (error) {
        logger.error('[CXone] Reconnection failed:', error);
        setIsSessionInvalid(true);
        setState((prev) => ({
          ...prev,
          connectionError: 'Failed to reconnect. Please try again.',
        }));
      }
    } else {
      logger.debug('[CXone] No stored voice preference, showing selector...');
      setNeedsVoicePreference(true);
      setState(initialCxoneState);
    }

    setIsReconnecting(false);
  }, [refs, initializeWithVoicePreference, setNeedsVoicePreference, setState]);

  const handleSessionError = useCallback(
    (error: Error | string) => {
      if (!isCxoneSessionError(error)) {
        const errorMessage = error instanceof Error ? error.message : error;
        logger.debug('[CXone] Non-session error, ignoring:', errorMessage);
        return;
      }

      const errorMessage = error instanceof Error ? error.message : error;
      logger.warn('[CXone] Session error detected:', errorMessage);
      refs.initializedRef.current = false;

      if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptRef.current++;
        logger.debug(
          `[CXone] Auto-reconnect attempt ${reconnectAttemptRef.current}/${MAX_RECONNECT_ATTEMPTS}`
        );
        void reconnect();
      } else {
        logger.debug('[CXone] Max reconnect attempts reached, showing reconnect UI');
        setIsSessionInvalid(true);
        setState((prev) => ({
          ...prev,
          isInitialized: false,
          isConnected: false,
          connectionError: 'Session expired. Click Reconnect to continue.',
        }));
      }
    },
    [refs, reconnect, setState]
  );

  const resetRecoveryState = useCallback(() => {
    reconnectAttemptRef.current = 0;
    setIsSessionInvalid(false);
    setIsReconnecting(false);
  }, []);

  return {
    isSessionInvalid,
    isReconnecting,
    reconnect,
    handleSessionError,
    resetRecoveryState,
  };
}
